import { apiFetch } from '../api';
import type {
  EmailAccountDetail,
  EmailAccountsListResponse,
  MailAccountGroupItem,
  MailAccountGroupsResponse,
} from '@/types/api';

export type { EmailAccountDetail, EmailAccountsListResponse, MailAccountGroupItem };

// ── List ──────────────────────────────────────────────────────────────────────

export interface EmailAccountsQuery {
  workspace_id: string;
  provider?: string;
  providers?: string;
  domain_id?: string;
  status?: string;
  group_id?: string;
  available?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchEmailAccounts(params: EmailAccountsQuery): Promise<EmailAccountsListResponse> {
  return apiFetch('/v1/email-accounts', {
    method: 'GET',
    query: params as unknown as Record<string, string | number | boolean | null | undefined>,
  });
}

// ── Detail ────────────────────────────────────────────────────────────────────

export function fetchEmailAccount(id: string, workspace_id: string): Promise<EmailAccountDetail> {
  return apiFetch(`/v1/email-accounts/${id}`, { method: 'GET', query: { workspace_id } });
}

// ── Actions ───────────────────────────────────────────────────────────────────

export function pauseEmailAccount(id: string, workspace_id: string): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/v1/email-accounts/${id}/pause`, { method: 'POST', body: { workspace_id } });
}

export function resumeEmailAccount(id: string, workspace_id: string): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/v1/email-accounts/${id}/resume`, { method: 'POST', body: { workspace_id } });
}

export function detachEmailAccount(id: string, workspace_id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/email-accounts/${id}/detach`, { method: 'POST', body: { workspace_id } });
}

export function replaceEmailAccountGroups(id: string, workspace_id: string, group_ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/email-accounts/${id}/groups`, { method: 'POST', body: { workspace_id, group_ids } });
}

export function syncEmailAccounts(workspace_id: string): Promise<{ task_id: string }> {
  return apiFetch('/v1/email-accounts/sync', { method: 'POST', body: { workspace_id } });
}

// ── Groups ────────────────────────────────────────────────────────────────────

export function fetchMailAccountGroups(workspace_id: string): Promise<MailAccountGroupsResponse> {
  return apiFetch('/v1/mail-account-groups', { method: 'GET', query: { workspace_id } });
}

export function createMailAccountGroup(workspace_id: string, name: string, color?: string): Promise<{ group: MailAccountGroupItem }> {
  return apiFetch('/v1/mail-account-groups', { method: 'POST', body: { workspace_id, name, color } });
}

export function updateMailAccountGroup(id: string, workspace_id: string, fields: { name?: string; color?: string }): Promise<{ group: MailAccountGroupItem }> {
  return apiFetch(`/v1/mail-account-groups/${id}`, { method: 'PATCH', body: { workspace_id, ...fields } });
}

export function deleteMailAccountGroup(id: string, workspace_id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/mail-account-groups/${id}?workspace_id=${workspace_id}`, { method: 'DELETE' });
}

export function bulkAddToGroup(groupId: string, workspace_id: string, account_ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/mail-account-groups/${groupId}/accounts`, { method: 'POST', body: { workspace_id, account_ids } });
}

export function bulkRemoveFromGroup(groupId: string, workspace_id: string, account_ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/mail-account-groups/${groupId}/accounts`, { method: 'DELETE', body: { workspace_id, account_ids } });
}
